<template>
    <div
        :class="[
            moreInfoNeeded
                ? 'rounded-lg bg-z-primary-25 px-3 py-5 ring-z-primary-500 ring-2'
                : '',
            'ease-out transition-all duration-250',
        ]"
    >
        <SelectLocation v-model="selectedLocation" :options="locationOptions" />

        <Transition
            enter-active-class="duration-300 ease-out"
            enter-from-class="transform -translate-y-1"
            enter-to-class="opacity-100"
            leave-active-class="duration-50 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                v-if="selectedLocation === 'in-person'"
                class="mt-4 gap-1 grid items-center"
            >
                <Label for="name">{{
                    $t("event_types.location.address")
                }}</Label>
                <Input
                    id="name"
                    v-model="address"
                    :placeholder="
                        $t('event_types.location.address_placeholder')
                    "
                />
            </div>
        </Transition>

        <Transition
            enter-active-class="duration-300 ease-out"
            enter-from-class="transform -translate-y-1"
            enter-to-class="opacity-100"
            leave-active-class="duration-50 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                v-if="selectedLocation === 'phone'"
                class="mt-4 gap-1 grid items-center"
            >
                <Label for="name">{{
                    $t("event_types.location.phone_label")
                }}</Label>
                <Input
                    id="name"
                    v-model="phone"
                    :placeholder="$t('event_types.location.phone_placeholder')"
                />
            </div>
        </Transition>

        <Transition
            enter-active-class="duration-300 ease-out"
            enter-from-class="transform -translate-y-1"
            enter-to-class="opacity-100"
            leave-active-class="duration-50 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div
                v-if="moreInfoNeeded && hasSomethingToSave"
                class="mt-4 flex justify-end"
            >
                <Button @click="onSave">{{ $t("save") }}</Button>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import SelectLocation from "./SelectLocation.vue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ref, watch, computed } from "vue";
import { Button } from "@/components/ui/button";
import axios from "axios";
import route from "ziggy-js";
import { EventType } from "../../../types/EventType";
import { useLocationOptions } from "../../../useLocationOptions";
import { LocationIntegrations } from "@/types/EventTypes/Types/Location";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";

const props = defineProps<{
    eventType: EventType;
    locationIntegrations: LocationIntegrations;
}>();

const { t } = useI18n();

const selectedLocation = ref(
    props.eventType.locations?.[0]?.type || "google-meet",
);
const address = ref("");
const phone = ref("");

const { locationsBasedOnIntegrations: locationOptions } = useLocationOptions(
    props.locationIntegrations,
);

if (selectedLocation.value === "in-person") {
    address.value = props.eventType.locations?.[0].value || "";
}

if (selectedLocation.value === "phone") {
    phone.value =
        props.eventType.locations?.[0].value ||
        t("event_types.location.phone_default");
}

const oldAddressValue = address.value;
const oldPhoneValue = phone.value;

const hasSomethingToSave = ref(false);

watch(selectedLocation, (location) => {
    if (
        location === "zoom" ||
        location === "google-meet" ||
        location === "microsoft-teams" ||
        location === "no-location" ||
        location === "phone"
    ) {
        saveLocation(location);
    }
});

watch(address, (newV) => {
    if (oldAddressValue === newV) {
        hasSomethingToSave.value = false;
    } else {
        hasSomethingToSave.value = true;
    }
});

watch(phone, (newV) => {
    if (oldPhoneValue === newV) {
        hasSomethingToSave.value = false;
    } else {
        hasSomethingToSave.value = true;
    }
});

function saveLocation(locationName: string) {
    let location = {};

    if (locationName === "in-person") {
        location = {
            type: "in-person",
            value: address.value,
            default: true,
        };
    } else if (locationName === "phone") {
        location = {
            type: "phone",
            value:
                phone.value === ""
                    ? t("event_types.location.phone_default")
                    : phone.value,
            default: true,
        };
    } else if (locationName === "google-meet") {
        location = {
            type: "google-meet",
            value: "google-meet",
            default: true,
        };
    } else if (locationName === "zoom") {
        location = {
            type: "zoom",
            value: "zoom",
            default: true,
        };
    } else if (locationName === "microsoft-teams") {
        location = {
            type: "microsoft-teams",
            value: "microsoft-teams",
            default: true,
        };
    } else if (locationName === "no-location") {
        location = {
            type: "no-location",
            value: "no-location",
            default: true,
        };
    }

    axios
        .patch(
            route("app.event-types.patch", {
                eventType: props.eventType.id,
            }),
            {
                locations: [location],
            },
        )
        .then(() => {
            window.location.reload();
        });
}

function onSave() {
    saveLocation(selectedLocation.value);
    hasSomethingToSave.value = false;
}

const moreInfoNeeded = computed(() => {
    return (
        selectedLocation.value === "in-person" ||
        selectedLocation.value === "phone"
    );
});
</script>
